import { useState, useEffect }      from 'react';
import { getTextColorByBackground, formatNumberBR } from '../../../../utils/stylesUtils'; 
import EditableCell                 from './EditableCell';
import api                          from '../../../../services/api';
import { usePxoPage }               from '../../../../contexts/PxoPageContext';

const PxoProdutividadeTable = ({
    isEditable,
    tableData, 
    allowNames
}) => {
    
    const { pxoProdutividadeColumns, pxoProdutividadeRows } = tableData

    const backgroundColor   = tableData.hexColor
    const fontColor         = getTextColorByBackground(backgroundColor)

    const { handleUpdateProdutividadeTable, handleUpdateOciosidadeCalculations } = usePxoPage()

    const [ _isDragging,         _setIsDragging         ] = useState(false)
    const [ _selectionStart,     _setSelectionStart     ] = useState(null) // {row, col}
    const [ _selectionEnd,       _setSelectionEnd       ] = useState(null) // {row, col}
    const [ _isEditingSelection, _setIsEditingSelection ] = useState(false)
    const [ _fillValue,          _setFillValue          ] = useState('')
    const [ _isSaving,           _setIsSaving           ] = useState(false)

    const getRowClass = (row) => {
        if (row.rowType === 'grand_total')  return 'bg-[#002A68] text-white font-bold ';
        if (row.rowType === 'bank_total' )  return 'border-b border-green-500 font-bold';
        if (row.rowType === 'yard_total' )  return 'bg-[#7B8C96] font-semibold text-white';
        return '';
    };

    const _isCellSelected = (rowIndex, colIndex) => {

        if (!_selectionStart || !_selectionEnd) return false

        const minRow = Math.min(_selectionStart.row, _selectionEnd.row)
        const maxRow = Math.max(_selectionStart.row, _selectionEnd.row)
        const minCol = Math.min(_selectionStart.col, _selectionEnd.col)
        const maxCol = Math.max(_selectionStart.col, _selectionEnd.col)

        return rowIndex >= minRow && rowIndex <= maxRow && colIndex >= minCol && colIndex <= maxCol
    }

    const _handleCellMouseDown = (e, rowIndex, colIndex, isTotal) => {

        if (!isEditable || isTotal || _isEditingSelection) return

        e.preventDefault() // evita seleção de texto nativa do navegador ao arrastar

        _setIsDragging(true)
        _setSelectionStart({ row: rowIndex, col: colIndex })
        _setSelectionEnd({ row: rowIndex, col: colIndex })
    }

    const _handleCellMouseEnter = (rowIndex, colIndex, isTotal) => {

        if (!_isDragging || isTotal) return

        _setSelectionEnd({ row: rowIndex, col: colIndex })
    }

    const _getSelectedValueItems = () => {

        if (!_selectionStart || !_selectionEnd) return []

        const minRow = Math.min(_selectionStart.row, _selectionEnd.row)
        const maxRow = Math.max(_selectionStart.row, _selectionEnd.row)
        const minCol = Math.min(_selectionStart.col, _selectionEnd.col)
        const maxCol = Math.max(_selectionStart.col, _selectionEnd.col)

        const items = []

        pxoProdutividadeRows.forEach((row, rowIndex) => {

            if (row.isTotalRow) return
            if (rowIndex < minRow || rowIndex > maxRow) return

            const values = row.pxoProdutividadeValues ?? []

            values.forEach((valueItem, colIndex) => {
                if (colIndex < minCol || colIndex > maxCol) return
                items.push(valueItem)
            })
        })

        return items
    }

    const _handleCancelSelection = () => {
        _setSelectionStart(null)
        _setSelectionEnd(null)
        _setIsEditingSelection(false)
        _setFillValue('')
    }

    const _handleApplyFillValue = async () => {

        const parsedValue = _fillValue.trim()

        if (parsedValue === '') {
            _handleCancelSelection()
            return
        }

        const items = _getSelectedValueItems()

        _setIsSaving(true)

        try {

            await Promise.all(
                items.map((item) =>
                    api.put(`/updatePxoProdutividadeValue/${item.id}`, { newValue: parsedValue })
                )
            )

        } catch (error) {
            console.error('Erro ao atualizar valores em lote:', error)
        }

        handleUpdateProdutividadeTable()
        handleUpdateOciosidadeCalculations()

        _setIsSaving(false)
        _handleCancelSelection()
    }

    const _handleFillKeyDown = (e) => {

        if (e.key === 'Enter') {
            e.preventDefault()
            _handleApplyFillValue()
        }

        if (e.key === 'Escape') {
            e.preventDefault()
            _handleCancelSelection()
        }
    }

    useEffect(() => {

        const handleMouseUp = () => {

            if (!_isDragging) return

            _setIsDragging(false)

            const isMultiCell = _selectionStart && _selectionEnd &&
                (_selectionStart.row !== _selectionEnd.row || _selectionStart.col !== _selectionEnd.col)

            if (isMultiCell) {
                _setIsEditingSelection(true)
                _setFillValue('')
            } else {
                _setSelectionStart(null)
                _setSelectionEnd(null)
            }
        }

        window.addEventListener('mouseup', handleMouseUp)
        return () => window.removeEventListener('mouseup', handleMouseUp)

    }, [_isDragging, _selectionStart, _selectionEnd])

    const selectedCount = _isEditingSelection ? _getSelectedValueItems().length : 0

    return (
        <div>
            <table className={`shadow-md ${_isDragging ? 'select-none' : ''}`}>

                <thead>
                    <tr
                        style={{ backgroundColor, color: fontColor }}
                    >   
                        {allowNames && (
                            <>
                                <th></th>
                                <th></th>
                            </>
                        )}
                                                    
                        
                        {pxoProdutividadeColumns.map((column, index) => (
                            <th key={index}>   
                            
                                <div className="flex flex-col px-2" >
                                    
                                    <span>{column.name}</span>
                                
                                    <span className="font-normal text-[10px]">
                                        {column.timeInDays ? column.timeInDays : <>&nbsp;</>}
                                    </span>

                                </div>
                            </th>
                        ))}
                        
                    </tr>
                </thead>

                <tbody>
                    {
                        pxoProdutividadeRows.map((row, j) => {
                            
                            const isTotal = row.isTotalRow;

                            const terminalName              = row.ownerTerminal?.name ?? '';
                            const productBranchName         = row.ownerProductBranch?.name ?? '';
                            const pxoProdutividadeValues    = row.pxoProdutividadeValues ?? [];

                            return (
                                <tr
                                    key={j}
                                    className={getRowClass(row)}
                                >
                                   {allowNames && (
                                        isTotal ? (
                                            <>
                                                <td colSpan={2} className="text-left px-1 pr-2  text-[14px]">
                                                    {productBranchName}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="text-left px-1 pr-2  text-[14px]">
                                                    {terminalName}
                                                </td>
                                                <td className="text-left text-[14px]">
                                                    {productBranchName}
                                                </td>
                                            </>
                                        )
                                    )}

                                   {pxoProdutividadeValues.map((valueItem, i) => (

                                        isEditable ? (
                                            (isTotal) ? (
                                                <td key={i} className="text-right px-1 font-bold text-[14px]">
                                                    {formatNumberBR(valueItem.value)}
                                                </td>
                                            ) : (
                                                <EditableCell 
                                                    key={i} 
                                                    pxoProdutividadeTableValue={valueItem} 
                                                    isSelected={_isCellSelected(j, i)}
                                                    isFillModeActive={_isEditingSelection}
                                                    onCellMouseDown={(e) => _handleCellMouseDown(e, j, i, isTotal)}
                                                    onCellMouseEnter={() => _handleCellMouseEnter(j, i, isTotal)}
                                                />
                                            )
                                        ): (
                                            <td key={i} className="text-right px-1 text-[14px]">
                                                {formatNumberBR(valueItem.value)}
                                            </td>
                                        )
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
            </table>

            {_isEditingSelection && (
                <div className="flex items-center gap-2 mt-2 p-2 border border-gray-300 rounded bg-gray-50 w-fit">
                    
                    <span className="text-[12px] text-gray-600">
                        {selectedCount} células selecionadas
                    </span>

                    <input
                        type="text"
                        autoFocus
                        value={_fillValue}
                        onChange={(e) => _setFillValue(e.target.value)}
                        onKeyDown={_handleFillKeyDown}
                        placeholder="Digite o valor e pressione Enter"
                        disabled={_isSaving}
                        className="border border-gray-300 rounded px-2 py-1 text-[13px] outline-none focus:border-blue-500"
                    />

                    <button
                        className="btn btn-azul-prof text-[12px] px-2 py-1"
                        onClick={_handleApplyFillValue}
                        disabled={_isSaving}
                    >
                        {_isSaving ? 'Salvando...' : 'Aplicar'}
                    </button>

                    <button
                        className="text-[12px] text-gray-500 hover:text-gray-700 px-2"
                        onClick={_handleCancelSelection}
                        disabled={_isSaving}
                    >
                        Cancelar
                    </button>

                </div>
            )}
        </div>
    )

}

export default PxoProdutividadeTable